import { useEffect, useState } from 'react';
import { reverseEngineerTargetGPA } from '@gpa-scales/shared-logic/engine/scenarioModeling';
import type { Course, AppSettings } from '@gpa-scales/shared-types';
import { useAppStore } from '@/store/useAppStore';
import { ExpressionInput, ScrubInput } from 'calc-controls';

export function TargetGPATab({ courses, settings }: { courses: Course[]; settings: AppSettings }) {
  const pendingTargetGPA = useAppStore((s) => s.pendingTargetGPA);
  const clearPendingTargetGPA = useAppStore((s) => s.clearPendingTargetGPA);
  const [targetGPA, setTargetGPA] = useState(3.5);
  const [slots, setSlots] = useState(4);
  const [result, setResult] = useState<{ possible: boolean; requiredGrades: string[]; projectedGPA: number } | null>(null);

  const calculate = (overrideTarget?: number) => {
    const target = overrideTarget ?? targetGPA;
    const res = reverseEngineerTargetGPA(
      courses,
      slots,
      target,
      'UC Capped Weighted GPA',
      settings
    );
    setResult(res);
  };

  // FE-E1 — consume a pending target GPA seeded from the Benchmarks view.
  // On mount and whenever pendingTargetGPA becomes non-null, seed the local
  // target, run the calculation, then clear the pending value.
  useEffect(() => {
    if (pendingTargetGPA !== null) {
      setTargetGPA(pendingTargetGPA);
      calculate(pendingTargetGPA);
      clearPendingTargetGPA();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingTargetGPA]);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="target-gpa" className="mb-1 block text-xs text-[var(--color-text-secondary)]" title="The UC Capped Weighted GPA you are aiming to reach.">Target GPA</label>
          <ExpressionInput
            value={targetGPA}
            onChange={setTargetGPA}
            id="target-gpa"
            min={0}
            max={5}
            decimals={2}
            inline
          />
        </div>
        <div>
          <label htmlFor="remaining-slots" className="mb-1 block text-xs text-[var(--color-text-secondary)]" title="How many full-credit courses you still expect to take before applications.">Remaining Slots</label>
          <ScrubInput
            value={slots}
            onChange={setSlots}
            label="Remaining Slots"
            min={1}
            max={20}
            decimals={0}
            inline
          />
        </div>
      </div>
      <button onClick={() => calculate()} data-testid="scenario-calculate" className="rounded-md bg-success px-3 py-1.5 text-xs font-medium text-white">
        Calculate
      </button>
      {result && (
        <div className={`rounded border p-3 text-xs ${result.possible ? 'bg-success-light border-success' : 'bg-error-light border-error'}`}>
          <p className="font-medium">
            {result.possible
              ? `Achievable! Minimum grades needed: ${result.requiredGrades.join(', ')}`
              : `Not achievable. Even with all A's, the projected GPA is ${result.projectedGPA.toFixed(2)}.`}
          </p>
          {result.possible && (
            <p className="mt-1 text-[var(--color-text-secondary)]">Projected GPA with those grades: {result.projectedGPA.toFixed(2)}</p>
          )}
        </div>
      )}
    </div>
  );
}
